/**
 * LANCER PLUSIEURS REQUÊTES À LA FOIS, MAIS PAS TOUTES.
 *
 * ⚠️ POURQUOI UNE LIMITE, ET PAS UN `Promise.all` NU.
 *
 * Les pages d'un gabarit se chargeaient une par une, chacune attendant la précédente :
 * sur un mandat de quatre pages, quatre fois 138 ms au lieu d'une. Le réflexe est
 * `Promise.all` — et il échangerait une attente contre une saturation. Chaque image
 * demandée est une RASTÉRISATION côté serveur, ~100 ms de processeur sur la même
 * machine : dix pages lancées ensemble, c'est une seconde de CPU d'un coup, pour un
 * écran, pendant que le reste de l'application attend son tour.
 *
 * Trois à la fois couvrent le cas courant — un mandat fait une à quatre pages — sans
 * jamais transformer un écran en pointe de charge.
 *
 * ⚠️ ET CHAQUE RÉSULTAT EST RENDU DÈS QU'IL ARRIVE.
 *
 * La version en série avait une qualité qu'on ne veut pas perdre : la première page
 * s'affichait pendant que les suivantes se calculaient, au lieu d'un écran vide jusqu'à
 * la dernière. `enParallele` rappelle donc `surChaque` au fil de l'eau, dans l'ordre où
 * les réponses reviennent — pas au bout.
 */

/** Trois requêtes de front : le cas courant sans la pointe de charge. */
export const CONCURRENCE = 3;

/**
 * Applique `tache` à chaque élément, `limite` à la fois, en rappelant `surChaque`
 * au fur et à mesure. Rend le nombre d'éléments réellement traités.
 *
 * `arreter()` est consulté avant chaque lancement : un changement de gabarit ne doit pas
 * continuer à peupler l'écran avec les pages du précédent.
 */
export default async function enParallele(elements, tache, {
  limite = CONCURRENCE, surChaque = () => {}, arreter = () => false,
} = {}) {
  const file = [...elements];
  let faits = 0;

  const ouvrier = async () => {
    while (file.length) {
      if (arreter()) return;
      const element = file.shift();
      const resultat = await tache(element);
      if (arreter()) return;
      surChaque(element, resultat);
      faits += 1;
    }
  };

  await Promise.all(Array.from({ length: Math.min(limite, elements.length) }, ouvrier));
  return faits;
}
